import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { QbPaginationDto } from '../../queries/query-editor/qb-pagination.dto';
import { Pagination } from '../pagination/pagination';
import { Utils } from '../utils';
import { PaginationQuery } from '../../queries/query-editor/query.interfaces';

export type FileResponse = { filename: string; data: Blob; headers: HttpHeaders };

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private readonly http: HttpClient) {}

  public async get<T>(url: string): Promise<T> {
    return firstValueFrom(this.http.get<T>(url));
  }

  public async getFile(url: string): Promise<Blob> {
    return firstValueFrom(this.http.get(url, { responseType: 'blob' }));
  }

  public async getFileWithName(url: string): Promise<FileResponse> {
    return firstValueFrom(
      this.http.get(url, { observe: 'response', responseType: 'blob' }).pipe(
        mergeMap((res: HttpResponse<Blob>) =>
          of({
            filename: this.fname(res.headers),
            data: res.body,
            headers: res.headers,
          }),
        ),
      ),
    );
  }

  public async postWithFileResponse<T = any>(url: string, body: T): Promise<FileResponse> {
    return firstValueFrom(
      this.http.post(url, Utils.stringNormalizer(body), { observe: 'response', responseType: 'blob' }).pipe(
        mergeMap((res: HttpResponse<Blob>) =>
          of({
            filename: this.fname(res.headers),
            data: res.body,
            headers: res.headers,
          }),
        ),
      ),
    );
  }

  public async post<T, K>(url: string, body: K): Promise<T> {
    return firstValueFrom(this.http.post<T>(url, Utils.stringNormalizer(body)));
  }

  public async postString<K>(url: string, body: K): Promise<string> {
    return firstValueFrom(this.http.post(url, Utils.stringNormalizer(body), { responseType: 'text' }));
  }

  public async put<T, K>(url: string, body: K): Promise<T> {
    return firstValueFrom(this.http.put<T>(url, Utils.stringNormalizer(body)));
  }

  public async getPaginated<T>(url: string): Promise<Pagination<T>> {
    return firstValueFrom(this.http.get<Pagination<T>>(url).pipe(map((res: Pagination<T>) => Pagination.fromJson<T>(res))));
  }

  public async getPaginatedQuery<T>(url: string): Promise<PaginationQuery<T>> {
    return firstValueFrom(this.http.get<PaginationQuery<T>>(url).pipe(map((res: PaginationQuery<T>) => PaginationQuery.fromJson<T>(res))));
  }

  public async postPaginated<T, K = any>(url: string, body: K): Promise<Pagination<T>> {
    return firstValueFrom(
      this.http.post<Pagination<T>>(url, Utils.stringNormalizer(body)).pipe(map((res: Pagination<T>) => Pagination.fromJson<T>(res))),
    );
  }

  public async postPaginatedQuery<T, K = any>(url: string, body: K): Promise<PaginationQuery<T>> {
    return firstValueFrom(
      this.http.post<PaginationQuery<T>>(url, Utils.stringNormalizer(body)).pipe(map((res: PaginationQuery<T>) => PaginationQuery.fromJson<T>(res))),
    );
  }

  public async postPaginatedCustomQuery<T, K = any>(url: string, body: K): Promise<QbPaginationDto> {
    return firstValueFrom(this.http.post<QbPaginationDto>(url, body).pipe(map((res: QbPaginationDto) => QbPaginationDto.fromJson(res))));
  }

  public async delete<T>(url: string): Promise<T> {
    return firstValueFrom(this.http.delete<T>(url));
  }

  private fname(headers: HttpHeaders): string {
    if (headers) {
      let value: string = headers.get('content-disposition') || '';
      const regex: RegExp = /^attachment; filename\=/;
      if (regex.test(value)) {
        value = value.replace(regex, '');
      }
      return value;
    }
  }
}
